import {
  FetchSharePointSitesParams,
  IGetPoliciesByTypeParams,
  mapQueryAllRecipientsPolicies,
  mapQueryAllSendersPolicies,
  mapQueryAssigneesNameEmail,
  mapQueryAwsBucketFilters,
  mapQueryAwsBucketOwners,
  mapQueryDataSourceNames,
  mapQueryDrivesNames,
  mapQueryExternalUsersFilter,
  mapQueryJiraProjectIdNames,
  mapQueryPoliciesIDNamesByType,
  mapQueryProjectIdNames,
  mapQueryReportersNameEmail,
  mapQueryRopaProcessOwners,
  mapQueryTemplates,
  mapSharePointSites,
  queryAllRecipientsPolicies,
  queryAllSendersPolicies,
  queryAssigneesNameEmail,
  queryAwsBucketFilters,
  queryAwsBucketOwners,
  queryDataSourceNames,
  queryDrivesNames,
  queryExternalUsersFilter,
  queryJiraProjectIdNames,
  queryPoliciesIDNamesByType,
  queryProjectIdNames,
  queryReportersNameEmail,
  queryRopaProcessOwners,
  querySharePointSites,
  queryTemplates,
  SharePointSite
} from './queries'
import { FilterParams } from '../../interfaces'
import service from '../../services/api/apiService'
import {
  mapQueryBLOBSParentsSummary,
  mapQueryDatabasesSummary,
  mapQueryTablesSummary,
  queryBLOBSParentsSummary,
  queryDatabasesSummary,
  queryTablesSummary
} from '../blobs/queries'
import { NameIdSummary } from '../ropa/ropaSlice'
import { Classification } from '../../services/api/apiTypes'
import graphqlService from '../../services/graphqlService'
import { CREATE_BY, DATA_SOURCE_TYPE_API_MAP } from '../../constants'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface ISearchFilterParams {
  key: string
  value: string
}
export interface IGetPaginatedFiltersParams {
  datasourceId?: string
  page?: number
  pageSize?: number
  filters?: FilterParams
  searchFilter?: ISearchFilterParams[]
}

export interface IFilterSearchParams {
  filterKey: string
  searchQuery: string
  searchField: string
  pageSize?: number
}
export interface ExternalUsersFiltersParam extends IGetPaginatedFiltersParams {
  alertId: string
}

export interface EmailFiltersParam extends IGetPaginatedFiltersParams {
  alertId?: string
}

export interface FileType {
  name: string
  types: string[]
}

interface FiltersState {
  isFiltersReceived: boolean
  dataSourcesTypes: string[]
  classes: Classification[]
  subClasses: string[]
  templates: { id: string; name: string }[]
  fileTypes: FileType[]
  datasources: NameIdSummary[]
  projects?: { id: string; name: string }[]
  drives?: { id: string; driveName: string; owner: string }[]
  drivesCount?: number
  bucketOwners?: { id: string; name: string }[]
  bucketOwnersCount?: number
  processOwners?: string[]
  imanageWorkspaces?: ImanageWorkspace[]
  awsBuckets?: string[]
  policies?: { id: string; name: string }[]
  reporters?: { email: string; name: string }[]
  assignees?: { email: string; name: string }[]
  blobParents?: { id: string; name: string }[]
  databases?: { id: string; name: string }[]
  tables?: { id: string; name: string }[]
  businessUnits?: NameIdSummary[]
  dataExtensions?: NameIdSummary[]
  datasourcesLoaded: boolean
  classFilterLoaded: boolean
  templatesLoaded: boolean
  externalUsersFilter?: { user: string; objectsCount: number }[]
  externalUsersFilterCount?: number
  sendersFilter?: string[]
  sendersFilterCount?: number
  recipientsFilter?: string[]
  recipientsFilterCount?: number
  sharePointSites?: SharePointSite[]
  sharePointSitesCount?: number
}

export const initialState: FiltersState = {
  isFiltersReceived: false,
  dataSourcesTypes: [...new Set(Object.values(DATA_SOURCE_TYPE_API_MAP))],
  classes: [],
  subClasses: [],
  templates: [],
  fileTypes: [
    { name: 'TEXT', types: ['txt'] },
    { name: 'PDF', types: ['pdf'] },
    { name: 'MS DOC', types: ['doc', 'docx'] },
    { name: 'EXCEL', types: ['xls', 'xlsx'] },
    { name: 'IMAGE', types: ['jpg', 'jpeg', 'png'] }
  ],
  datasources: [],
  datasourcesLoaded: false,
  classFilterLoaded: false,
  templatesLoaded: false
}

export const fetchFilters = createAsyncThunk('filters/get', async () => {
  const results = await Promise.all([service.getClassificationsList()]).catch((error) => {
    throw error
  })

  return {
    classes: results[0]
  }
})

export const ACTION_PROJECTS_ID_NAMES_FETCH = 'projects/namesIds'
export const fetchProjectsIDNames = createAsyncThunk(ACTION_PROJECTS_ID_NAMES_FETCH, async () => {
  const result = await graphqlService.execute(queryProjectIdNames())
  return mapQueryProjectIdNames(result)
})

export const ACTION_AWS_BUCKET_OWNERS_FETCH = 'aws/bucketOwners'
export const fetchAwsBucketOwners = createAsyncThunk(
  ACTION_AWS_BUCKET_OWNERS_FETCH,
  async (params: IGetPaginatedFiltersParams) => {
    const result = await graphqlService.execute(queryAwsBucketOwners(params))
    return mapQueryAwsBucketOwners(result)
  }
)

export const ACTION_ROPA_PROCESS_OWNERS_FETCH = 'ropa/process-owners'
export const fetchRopaProcessOwners = createAsyncThunk(
  ACTION_ROPA_PROCESS_OWNERS_FETCH,
  async () => {
    const result = await graphqlService.execute(queryRopaProcessOwners())
    return mapQueryRopaProcessOwners(result)
  }
)

export const ACTION_AWS_S3_BUCKETS_FETCH = 'aws-s3/buckets'
export const fetchAwsBucketFilters = createAsyncThunk(
  ACTION_AWS_S3_BUCKETS_FETCH,
  async (params: IGetPaginatedFiltersParams) => {
    const result = await graphqlService.execute(queryAwsBucketFilters(params))
    return mapQueryAwsBucketFilters(result)
  }
)

export const ACTION_JIRA_PROJECTS_ID_NAMES_FETCH = 'jira/projects/namesIds'
export const fetchJiraProjectsIDNames = createAsyncThunk(
  ACTION_JIRA_PROJECTS_ID_NAMES_FETCH,
  async (id: string) => {
    const result = await graphqlService.execute(queryJiraProjectIdNames(id))
    return mapQueryJiraProjectIdNames(result)
  }
)

export const ACTION_JIRA_REPORTERS_NAME_EMAIL_FETCH = 'jira/reporter/name-email'
export const fetchReportersNameEmail = createAsyncThunk(
  ACTION_JIRA_REPORTERS_NAME_EMAIL_FETCH,
  async () => {
    const result = await graphqlService.execute(queryReportersNameEmail())
    return mapQueryReportersNameEmail(result)
  }
)

export const ACTION_JIRA_ASSIGNEE_NAME_EMAIL_FETCH = 'jira/assignee/name-email'
export const fetchAssigneesNameEmail = createAsyncThunk(
  ACTION_JIRA_ASSIGNEE_NAME_EMAIL_FETCH,
  async () => {
    const result = await graphqlService.execute(queryAssigneesNameEmail())
    return mapQueryAssigneesNameEmail(result)
  }
)

export const ACTION_FETCH_POLICIES_EXTERNAL_USERS_FILTER = 'policies/external-users'
export const fetchPoliciesExternalUsersFilter = createAsyncThunk(
  ACTION_FETCH_POLICIES_EXTERNAL_USERS_FILTER,
  async (params: ExternalUsersFiltersParam) => {
    const result = await graphqlService.execute(queryExternalUsersFilter(params))
    return mapQueryExternalUsersFilter(result)
  }
)

export const ACTION_FETCH_POLICIES_SENDERS_FILTER = 'policiesFilter/senders'
export const fetchPoliciesSendersFilters = createAsyncThunk(
  ACTION_FETCH_POLICIES_SENDERS_FILTER,
  async (params: EmailFiltersParam) => {
    const result = await graphqlService.execute(queryAllSendersPolicies(params))
    return mapQueryAllSendersPolicies(result)
  }
)

export const ACTION_FETCH_POLICIES_RECIPIENTS_FILTER = 'policiesFilter/recipients'
export const fetchPoliciesRecipientsFilters = createAsyncThunk(
  ACTION_FETCH_POLICIES_RECIPIENTS_FILTER,
  async (params: EmailFiltersParam) => {
    const result = await graphqlService.execute(queryAllRecipientsPolicies(params))
    return mapQueryAllRecipientsPolicies(result)
  }
)

export const fetchClassFilters = createAsyncThunk('classFilters/get', async () => {
  const results = await Promise.all([service.getClassificationsList()]).catch((error) => {
    throw error
  })

  return {
    classes: results[0]
  }
})

export const ACTION_FETCH_DRIVES_FILTERS = 'driveFilters/get'
export const fetchDriveFilters = createAsyncThunk(
  ACTION_FETCH_DRIVES_FILTERS,
  async (params: IGetPaginatedFiltersParams) => {
    const resultsRaw = await graphqlService.execute(queryDrivesNames(params))
    return mapQueryDrivesNames(resultsRaw)
  }
)
export const fetchTemplateFilters = createAsyncThunk('templateFilters/get', async () => {
  const resultsRaw = await graphqlService.execute(queryTemplates())
  const result = mapQueryTemplates(resultsRaw)

  // ? Hide system generated templates
  result.list = result.list.filter((template) => template.createdBy !== CREATE_BY.System)

  return result
})
export const ACTION_DATASOURCENAMES_FETCH = 'attributes/datasourceNames'
export const fetchDatasourcesIDNames = createAsyncThunk(
  ACTION_DATASOURCENAMES_FETCH,
  async (params: FilterParams) => {
    const resultRaw = await graphqlService.execute(queryDataSourceNames(params))
    return mapQueryDataSourceNames(resultRaw)
  }
)

export const ACTION_POLICIES__NAMES_FETCH = 'policies/names'
export const fetchPoliciesIDNamesByType = createAsyncThunk(
  ACTION_POLICIES__NAMES_FETCH,
  async (params: IGetPoliciesByTypeParams) => {
    const resultRaw = await graphqlService.execute(queryPoliciesIDNamesByType(params))
    return mapQueryPoliciesIDNamesByType(resultRaw)
  }
)

export const ACTION_BLOBS_PARENTS_FETCH = 'blobs/parents'
export const fetchBLOBSParentsSummary = createAsyncThunk(
  ACTION_BLOBS_PARENTS_FETCH,
  async (params: FilterParams) => {
    const resultRaw = await graphqlService.execute(queryBLOBSParentsSummary(params))
    return mapQueryBLOBSParentsSummary(resultRaw)
  }
)

export const ACTION_DATATABASE_FILTERS_FETCH = 'databases/filters'
export const fetchDatabaseFilters = createAsyncThunk(
  ACTION_DATATABASE_FILTERS_FETCH,
  async (datasourceId: string) => {
    const resultRaw = await graphqlService.execute(queryDatabasesSummary(datasourceId))
    return mapQueryDatabasesSummary(resultRaw)
  }
)

export const ACTION_TABLE_FILTERS_FETCH = 'tables/filters'
export const fetchTablesFilters = createAsyncThunk(
  ACTION_TABLE_FILTERS_FETCH,
  async (datasourceId: string) => {
    const resultRaw = await graphqlService.execute(queryTablesSummary(datasourceId))
    return mapQueryTablesSummary(resultRaw)
  }
)

export const ACTION_SFMC_TABLE_FILTERS_FETCH = 'tables/sfmcFilters'
export const fetchSfmcTablesFilters = createAsyncThunk(
  ACTION_SFMC_TABLE_FILTERS_FETCH,
  async (datasourceId: string) => {
    const resultRaw = await service.getSfmcFilters({ datasourceId })

    return {
      businessUnits: Object.entries(resultRaw.businessUnits).map(([key, value]) => ({
        id: key,
        name: value
      })) as NameIdSummary[],
      dataExtensions: Object.entries(resultRaw.dataExtensions).map(([key, value]) => ({
        id: key,
        name: value
      })) as NameIdSummary[]
    }
  }
)

export const ACTION_FETCH_SHAREPOINT_SITES = 'sharePoint/sites'
export const fetchSharePointSites = createAsyncThunk(
  ACTION_FETCH_SHAREPOINT_SITES,
  async (params: FetchSharePointSitesParams) => {
    const resultRaw = await graphqlService.execute(querySharePointSites(params))
    return mapSharePointSites(resultRaw)
  }
)

export type ImanageWorkspace = {
  id: string
  name: string
}

export const ACTION_FETCH_IMANAGE_WORKSPACES = 'iManage/workspaces'

export const fetchImanageWorkspaces = createAsyncThunk(
  ACTION_FETCH_IMANAGE_WORKSPACES,
  async (datasourceId: string) => await service.getImanageWorkspaces(datasourceId)
)

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setSubClasses: (state, { payload }) => {
      state.subClasses = payload
    },
    resetProjectsIDNames: (state) => {
      state.projects = initialState.projects
    },
    resetBLOBSParentsSummary: (state) => {
      state.blobParents = initialState.blobParents
    },
    resetDatasourcesIDNames: (state) => {
      state.datasources = initialState.datasources
    },
    resetProcessOwners: (state) => {
      state.processOwners = initialState.processOwners
    },
    resetExternalUsersFilter: (state) => {
      state.externalUsersFilter = initialState.externalUsersFilter
      state.externalUsersFilterCount = initialState.externalUsersFilterCount
    },
    resetSendersFilter: (state) => {
      state.sendersFilter = initialState.sendersFilter
      state.sendersFilterCount = initialState.sendersFilterCount
    },
    resetRecipientsFilter: (state) => {
      state.recipientsFilter = initialState.recipientsFilter
      state.recipientsFilterCount = initialState.recipientsFilterCount
    },
    resetSharePointSites: (state) => {
      state.sharePointSites = initialState.sharePointSites
      state.sharePointSitesCount = initialState.sharePointSitesCount
    },
    resetDrivesFilters: (state) => {
      state.drives = initialState.drives
      state.drivesCount = initialState.drivesCount
    },
    resetAwsBucketOwners: (state) => {
      state.bucketOwners = initialState.bucketOwners
      state.bucketOwnersCount = initialState.bucketOwnersCount
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilters.fulfilled, (state, { payload }) => {
      state.classes = payload.classes
      state.dataSourcesTypes = [...new Set(Object.values(DATA_SOURCE_TYPE_API_MAP))]
      state.isFiltersReceived = true
    })
    builder.addCase(fetchClassFilters.fulfilled, (state, { payload }) => {
      state.classes = payload.classes
      state.classFilterLoaded = true
    })
    builder.addCase(fetchDriveFilters.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0
      state.drives = resetOnFirstSearch ? payload.list : [...(state.drives ?? []), ...payload.list]
      state.drivesCount = payload.total
    })
    builder.addCase(fetchTemplateFilters.fulfilled, (state, { payload }) => {
      state.templates = payload.list
      state.templatesLoaded = true
    })
    builder.addCase(fetchDatasourcesIDNames.pending, (state) => {
      state.datasourcesLoaded = false
    })
    builder.addCase(fetchDatabaseFilters.fulfilled, (state, { payload }) => {
      state.databases = payload
    })
    builder.addCase(fetchTablesFilters.fulfilled, (state, { payload }) => {
      state.tables = payload
    })
    builder.addCase(fetchSfmcTablesFilters.fulfilled, (state, { payload }) => {
      state.businessUnits = payload.businessUnits
      state.dataExtensions = payload.dataExtensions
    })
    builder.addCase(fetchDatasourcesIDNames.rejected, (state) => {
      state.datasourcesLoaded = false
    })
    builder.addCase(fetchDatasourcesIDNames.fulfilled, (state, { payload }) => {
      state.datasources = payload.list
      state.datasourcesLoaded = true
    })
    builder.addCase(fetchPoliciesIDNamesByType.fulfilled, (state, { payload }) => {
      state.policies = payload.list
    })
    builder.addCase(fetchProjectsIDNames.fulfilled, (state, { payload }) => {
      state.projects = payload.list
    })
    builder.addCase(fetchJiraProjectsIDNames.fulfilled, (state, { payload }) => {
      state.projects = payload.list
    })
    builder.addCase(fetchReportersNameEmail.fulfilled, (state, { payload }) => {
      state.reporters = payload.list
    })
    builder.addCase(fetchAssigneesNameEmail.fulfilled, (state, { payload }) => {
      state.assignees = payload.list
    })
    builder.addCase(fetchAwsBucketOwners.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0
      state.bucketOwners = resetOnFirstSearch
        ? payload.list
        : [...(state.bucketOwners ?? []), ...payload.list]
      state.bucketOwnersCount = payload.total
    })
    builder.addCase(fetchRopaProcessOwners.fulfilled, (state, { payload }) => {
      state.processOwners = payload
    })
    builder.addCase(fetchAwsBucketFilters.fulfilled, (state, { payload }) => {
      state.awsBuckets = payload
    })
    builder.addCase(fetchBLOBSParentsSummary.fulfilled, (state, { payload }) => {
      state.blobParents = payload
    })
    builder.addCase(fetchPoliciesExternalUsersFilter.fulfilled, (state, { payload }) => {
      state.externalUsersFilter = [...(state.externalUsersFilter ?? []), ...payload.list]
      state.externalUsersFilterCount = payload.total
    })
    builder.addCase(fetchImanageWorkspaces.fulfilled, (state, { payload }) => {
      state.imanageWorkspaces = payload
    })
    builder.addCase(fetchPoliciesSendersFilters.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0
      state.sendersFilter = resetOnFirstSearch
        ? [...payload.list]
        : [...(state.sendersFilter ?? []), ...payload.list]
      state.sendersFilterCount = payload.total
    })
    builder.addCase(fetchPoliciesRecipientsFilters.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0
      state.recipientsFilter = resetOnFirstSearch
        ? [...payload.list]
        : [...(state.recipientsFilter ?? []), ...payload.list]
      state.recipientsFilterCount = payload.total
    })
    builder.addCase(fetchSharePointSites.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0

      state.sharePointSites = resetOnFirstSearch
        ? payload.sharePointSites
        : [...(state.sharePointSites ?? []), ...payload.sharePointSites]
      state.sharePointSitesCount = payload.total
    })
  }
})
export const fileTypesFilterMap = (filterVal) => {
  return filterVal.map((fileType) => fileType.name)
}
export const {
  setSubClasses,
  resetProjectsIDNames,
  resetBLOBSParentsSummary,
  resetDatasourcesIDNames,
  resetProcessOwners,
  resetExternalUsersFilter,
  resetSendersFilter,
  resetRecipientsFilter,
  resetSharePointSites,
  resetDrivesFilters,
  resetAwsBucketOwners
} = filtersSlice.actions

export default filtersSlice.reducer
